'use client';

import { useQuery } from '@tanstack/react-query';
import Link from 'next/link';
import { ReactNode } from 'react';

import { getLaunchpoolApi } from '@flipster/apis/v1/launchpool';
import { LaunchpoolStatus } from '@flipster/apis/v1/launchpool/types';
import { getTradingCompetitionsListApi } from '@flipster/apis/v1/tradingCompetition';
import { CompetitionStatusEnum } from '@flipster/apis/v1/tradingCompetition/types';
import { useAuth } from '@flipster/features/src/shared/auth/state';
import { Badge } from '@flipster/react/design-system/badge';
import ChevronRightIcon from '@flipster/react/icon/generated/ChevronRightIcon';
import { css } from '@flipster/styled-system/css';
import { flex } from '@flipster/styled-system/patterns';
import { token } from '@flipster/styled-system/tokens';
import Num, { numberFormatter } from '@flipster/utils/num';
import segment from '@flipster/utils/segment';
import { MainEvent } from '@flipster/utils/segment/events/main';

import { maxWStyles } from '../_styles';
import Launchpool from '../assets/launchpool.webp';
import TradingCompetition from '../assets/trading-competition.webp';

interface Campaign {
  title: string;
  subTitle: string;
  description: ReactNode;
  image: string;
  link: string;
  isComingSoon: boolean;
  onClick: (title: string) => void;
}

const CardStyle = flex({
  flex: 1,
  direction: { base: 'column', tablet: 'row' },
  align: 'center',
  gap: { base: 12, tablet: 24 },
  px: { base: 16, tablet: 20 },
  py: { base: 24, tablet: 20 },
  borderColor: 'border.default.level-1',
  border: '1px solid',
  rounded: 12,
});

const CampaignCard = ({ title, subTitle, description, image, link, isComingSoon, onClick }: Campaign) => {
  if (isComingSoon)
    return (
      <li className={flex({ flex: 1, align: 'stretch' })} onClick={() => onClick(title)}>
        <div className={CardStyle}>
          <div className={css({ w: { base: 40, tablet: 64 }, h: { base: 40, tablet: 64 } })}>
            <img src={image} alt={title} width='100%' />
          </div>
          <div className={css({ flex: 1, textAlign: { base: 'center', tablet: 'left' } })}>
            <h3
              className={css({
                textStyle: 'Title/S',
                color: 'content.default.level-4',
              })}
            >
              {title}
            </h3>
            <p
              className={css({
                tabletDown: { display: 'none' },
                textStyle: 'Text/L/Regular',
                color: 'content.default.level-2',
                mt: 4,
              })}
            >
              {subTitle}
            </p>
            <Badge type='tint' color='promotion' css={{ mt: { base: 4, tablet: 12 } }}>
              Coming soon
            </Badge>
          </div>
        </div>
      </li>
    );

  return (
    <li className={flex({ flex: 1, align: 'stretch' })}>
      <Link href={link} className={CardStyle} onClick={() => onClick(title)}>
        <div className={css({ w: { base: 40, tablet: 64 }, h: { base: 40, tablet: 64 } })}>
          <img src={image} alt={title} width='100%' />
        </div>
        <div className={css({ flex: 1, textAlign: { base: 'center', tablet: 'left' } })}>
          <h3
            className={css({
              textStyle: 'Title/S',
              color: 'content.default.level-4',
            })}
          >
            {title}
          </h3>
          <p
            className={css({
              tabletDown: { display: 'none' },
              textStyle: 'Text/L/Regular',
              color: 'content.default.level-2',
              mt: 4,
            })}
          >
            {subTitle}
          </p>
          <p
            className={css({
              textStyle: 'Text/M/Strong',
              color: 'content.accent.promotion',
              mt: { base: 4, tablet: 12 },
            })}
          >
            {description}
          </p>
        </div>
        <div className={css({ tabletDown: { display: 'none' } })}>
          <ChevronRightIcon size={24} color={token('colors.content.default.level-3')} />
        </div>
      </Link>
    </li>
  );
};

const LiveThisWeek = () => {
  const { isCookieLoggedIn } = useAuth();

  const { data: tradingCompetition } = useQuery({
    queryKey: ['LiveThisWeek', 'getTradingCompetitionsListApi', isCookieLoggedIn],
    queryFn: () => getTradingCompetitionsListApi(),
    select: (res) => {
      // Ongoing or Upcoming
      const comps = res.data.competitions.filter((competition) =>
        [CompetitionStatusEnum.Ongoing, CompetitionStatusEnum.Upcoming].includes(competition.competitionStatus),
      );
      // Max prize pool
      return comps.reduce(
        (acc, cur) => (Num(cur.prizePool).isGreaterThanOrEqualTo(Num(acc.prizePool)) ? cur : acc),
        comps[0],
      );
    },
  });

  const { data: launchpool } = useQuery({
    queryKey: ['LiveThisWeek', 'getLaunchpoolApi', isCookieLoggedIn],
    queryFn: () => getLaunchpoolApi(),
    select: (res) => {
      // Ongoing or Upcoming
      const pools = res.data.pools.filter((pool) =>
        [LaunchpoolStatus.Ongoing, LaunchpoolStatus.Upcoming].includes(pool.status),
      );
      // Max maximumUsdtApr
      return pools.reduce(
        (acc, cur) => (Num(cur.maximumUsdtApr ?? 0).isGreaterThanOrEqualTo(Num(acc.maximumUsdtApr ?? 0)) ? cur : acc),
        pools[0],
      );
    },
  });

  const campaigns = [
    {
      title: 'Trading competition',
      subTitle: 'Compete with other traders every week',
      description: `${numberFormatter(tradingCompetition?.prizePool)} USDT prize pool`,
      image: TradingCompetition.src,
      link: `/competitions/${tradingCompetition?.competitionUrl}`,
      isComingSoon: !tradingCompetition,
    },
    {
      title: 'Launchpool',
      subTitle: 'Stake to earn star project tokens',
      description: `Up to ${Num(launchpool?.maximumUsdtApr ?? 0)}% APR`,
      image: Launchpool.src,
      link: '/launchpool', // FIXME: replace with marketing page
      isComingSoon: !launchpool,
    },
  ];

  const onClickCampaign = (title: string) => {
    segment.track(MainEvent.IntroduceCampaignLiveBannerClick, {
      banner_title: title,
      event_location: 'main_page_view',
    });
  };

  return (
    <section className={css(maxWStyles, { mt: { base: 48, tablet: 24 }, mb: { base: 48, tablet: 64 } })}>
      <h2
        className={css({
          textStyle: 'Title/M',
          color: 'content.default.level-4',
          mb: { base: 16, tablet: 32 },
        })}
      >
        Live this week
      </h2>
      <ul className={flex({ gap: { base: 16, tablet: 20 } })}>
        {campaigns.map((campaign) => (
          <CampaignCard key={campaign.title} {...campaign} onClick={onClickCampaign} />
        ))}
      </ul>
    </section>
  );
};

export default LiveThisWeek;
